import React, { useState, useEffect } from "react";
import { C, fmt, text } from "../theme";
import { usePermissions } from "../PermissionContext";
import WatcherPanel from "../components/WatcherPanel";
import DocumentSection from "../components/DocumentSection";

import { Card, Btn, Notice, DocLink, StatusPill as Badge, ApprovalTrail, ReturnedBanner, rowAction } from "../components/ui";
import { api } from "../api";

export default function POPage({ initialId, onOpenDoc }) {
  const { me } = usePermissions();
  const [pos, setPOs] = useState([]);
  const [aors, setAORs] = useState([]);
  const [sel, setSel] = useState(initialId || null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [taxCodes, setTaxCodes] = useState([]);
  const [tenderThreshold, setTenderThreshold] = useState(null);
  const [vendors, setVendors] = useState([]);

  // Form state
  const [selectedAOR, setSelectedAOR] = useState(null);
  const [vendor, setVendor] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null); // { kind: "success" | "error", text }

  useEffect(() => {
    api.getProcurementPolicy().then((p) => setTenderThreshold(p.tenderThreshold)).catch(() => {});
    api.getReferenceLookup("vendors").then(v => setVendors(Array.isArray(v) ? v.filter(x => x.isActive !== false) : [])).catch(() => setVendors([]));
  }, []);

  const load = () => {
    setLoading(true);
    Promise.all([
      fetch("/api/pos").then(r => r.json()),
      fetch("/api/aors").then(r => r.json()),
    ]).then(([p, aor]) => {
      setPOs(p);
      setAORs(aor);
    }).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, []);
  // A non-OK response (e.g. 403 for roles without reference:view) must degrade
  // to "no tax codes", never reach state as an error object (it gets .filter'd).
  useEffect(() => { fetch("/api/reference/tax-codes").then(r => (r.ok ? r.json() : [])).then(d => setTaxCodes(Array.isArray(d) ? d : [])).catch(() => setTaxCodes([])); }, []);

  // Only approved AORs that don't already have a PO
  const availableAORs = aors.filter(aor => {
    if (aor.status !== "APPROVED") return false;
    const hasPO = pos.some(po => po.aorId === aor.id);
    return !hasPO;
  });

  const aorDetail = selectedAOR ? aors.find(p => p.id === selectedAOR) : null;
const handleSubmitPO = async (poId) => {
    try {
      const res = await fetch("/api/pos/" + poId + "/submit", { method: "POST" });
      const data = await res.json();
      if (!res.ok) { setNotice({ kind: "error", text: "Error: " + (data.error || "Failed") }); return; }
      setNotice({ kind: "success", text: data.message + "\nLayers: " + data.layers.join(" → ") });
      load();
      setSel(null);
    } catch (err) {
      setNotice({ kind: "error", text: "Error: " + err.message });
    }
  };
  const setLineTax = (poId, lineId, taxCodeId) => {
    setPOs(prev => prev.map(p => p.id !== poId ? p : { ...p, lineItems: p.lineItems.map(l => l.id === lineId ? { ...l, taxCodeId } : l) }));
  };
  const saveTaxCodes = async (po) => {
    const lines = (po.lineItems || []).map(l => ({ lineItemId: l.id, taxCodeId: l.taxCodeId }));
    const res = await fetch("/api/pos/" + po.id + "/tax-codes", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ lines }) });
    if (!res.ok) { setNotice({ kind: "error", text: "Error: " + (await res.json()).error }); return; }
    const updated = await res.json();
    setNotice({ kind: "success", text: "Tax codes saved. GST: " + fmt(updated.gstTotal || 0) });
    load();
  };
  const handleCreate = async () => {
    if (!selectedAOR || !vendor.trim()) {
      setNotice({ kind: "error", text: "Please select an AOR and enter a vendor name." });
      return;
    }
    setSubmitting(true);
    try {
      const aor = aors.find(p => p.id === selectedAOR);
      const res = await fetch("/api/pos", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          aorId: selectedAOR,
          vendor: vendor.trim(),
          requestorId: aor.requestorId,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        // The form stays mounted with its input intact so the user can fix and retry.
        setNotice({ kind: "error", text: "Error: " + (data.error || "Failed to create PO") });
        return;
      }
      // Land on the created PO's detail instead of back on the list (UX-12/52).
      setNotice({ kind: "success", text: "PO Created: " + data.poNumber + " (Draft)\nVendor: " + vendor + "\nTotal: " + fmt(data.total) + "\n\nReview the line items and tax codes below, then submit for approval." });
      setShowForm(false);
      setSelectedAOR(null);
      setVendor("");
      setSel(data.id);
      load();
    } catch (err) {
      setNotice({ kind: "error", text: "Error: " + err.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: C.gray500 }}>Loading...</div>;

  // ─── Create Form ───
  if (showForm) {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <Btn variant="outline" onClick={() => { setShowForm(false); setSelectedAOR(null); setVendor(""); }}>← Cancel</Btn>
          <h1 style={{ fontSize: text.h2, fontWeight: 700, margin: 0 }}>Create Purchase Order</h1>
        </div>

        <Notice notice={notice} onDismiss={() => setNotice(null)} />

        {/* Step 1: Select approved AOR */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 4 }}>Step 1: Select Approved AOR</div>
          <div style={{ fontSize: text.sm, color: C.gray500, marginBottom: 14 }}>POs are created from approved Approval of Needs Requests. Line items and budget are inherited from the AOR.</div>

          {availableAORs.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", background: C.warningBg, borderRadius: 8, border: `1px solid ${C.warningLight}` }}>
              <div style={{ fontSize: text.md, fontWeight: 700, color: C.warning }}>No Approved AORs Available</div>
              <div style={{ fontSize: text.sm, color: C.gray600, marginTop: 4 }}>All approved AORs already have POs, or there are no approved AORs yet. Create and approve an AOR first.</div>
            </div>
          ) : (
            <div>
              {availableAORs.map(aor => {
                const isSelected = selectedAOR === aor.id;
                return (
                  <div key={aor.id} onClick={() => setSelectedAOR(aor.id)} style={{
                    display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", marginBottom: 8,
                    borderRadius: 10, cursor: "pointer",
                    border: isSelected ? `2px solid ${C.primary}` : `2px solid ${C.gray200}`,
                    background: isSelected ? C.primaryBg : C.white,
                  }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                      border: isSelected ? `2px solid ${C.primary}` : `2px solid ${C.gray300}`,
                      background: isSelected ? C.primary : C.white,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: C.white, fontSize: text.sm,
                    }}>{isSelected ? "✓" : ""}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: text.lg, fontWeight: 700, color: C.gray1100 }}>
                        {aor.aorNumber} — {aor.description}
                      </div>
                      <div style={{ fontSize: text.sm, color: C.gray600, marginTop: 2 }}>
                        {aor.businessUnit} • {aor.budgetRef} • {aor.requestor?.displayName}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: text.h3, fontWeight: 700, color: C.gray1100 }}>{fmt(aor.total)}</div>
                      <div style={{ marginTop: 2 }}><Badge status={aor.budgetCheck?.status} /></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Step 2: AOR details + vendor */}
        {aorDetail && (
          <>
            {/* Tender-threshold nudge (UX-10): a direct PO at or above the
                threshold should have come through the tender path. */}
            {tenderThreshold != null && aorDetail.total >= tenderThreshold && !aorDetail.vendorEvaluation && (
              <Card style={{ marginBottom: 16, background: C.warningBg, border: `1px solid ${C.warningLight}` }}>
                <div style={{ fontSize: text.md, fontWeight: 700, color: C.warning, marginBottom: 4 }}>⚠ {fmt(aorDetail.total)} is at or above the {fmt(tenderThreshold)} tender threshold</div>
                <div style={{ fontSize: text.sm, color: C.gray700, lineHeight: 1.5 }}>
                  Purchases at this value normally go through the tender path: raise a <b>Tender Request</b> (ITQ/ITT) from this AOR,
                  run the <b>Vendor Evaluation</b>, and create the PO from the award. A direct PO here skips the vendor-selection record
                  and will still require the Tender Evaluation Form upload at submission.
                </div>
              </Card>
            )}
            <Card style={{ marginBottom: 16 }}>
              <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 14 }}>Step 2: Assign Vendor</div>
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="po-vendor-name" style={{ fontSize: text.sm, fontWeight: 600, color: C.gray700, display: "block", marginBottom: 4 }}>Vendor Name *</label>
                <input id="po-vendor-name" list="po-vendor-register" value={vendor} onChange={e => setVendor(e.target.value)} placeholder="e.g. Amazon Web Services, Dell Technologies, KPMG Services" style={{ width: "100%", padding: "10px 14px", border: `1px solid ${C.gray300}`, borderRadius: 6, fontSize: text.lg, fontFamily: "inherit", boxSizing: "border-box" }} />
                {/* Vendor register suggestions (UX-17); free text stays allowed. */}
                <datalist id="po-vendor-register">
                  {vendors.map(v => <option key={v.id} value={v.label}>{v.code}</option>)}
                </datalist>
                {vendors.length > 0 && <div style={{ fontSize: text.xs, color: C.gray500, marginTop: 4 }}>Suggestions come from the vendor register ({vendors.map(v => v.code).join(", ")}).</div>}
              </div>
            </Card>

            {/* Preview: line items from AOR */}
            <Card style={{ marginBottom: 16 }}>
              <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 4 }}>PO Preview (from {aorDetail.aorNumber})</div>
              <div style={{ fontSize: text.sm, color: C.gray500, marginBottom: 14 }}>These line items are inherited from the approved AOR and will appear on the PO.</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
                <thead><tr>{["Description", "Qty", "Unit Price", "Account", "Total"].map((h, i) => (
                  <th scope="col" key={i} style={{ padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
                ))}</tr></thead>
                <tbody>
                  {aorDetail.lineItems?.map((li, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                      <td style={{ padding: "10px 12px" }}>{li.description}</td>
                      <td style={{ padding: "10px 12px" }}>{li.quantity}</td>
                      <td style={{ padding: "10px 12px" }}>{fmt(li.unitPrice)}</td>
                      <td style={{ padding: "10px 12px", color: C.gray600 }}>{li.accountCode || "—"}</td>
                      <td style={{ padding: "10px 12px", fontWeight: 600 }}>{fmt(li.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ textAlign: "right", marginTop: 12, fontSize: text.h3, fontWeight: 700, color: C.gray1100 }}>
                PO Total: {fmt(aorDetail.total)}
              </div>
            </Card>

            {/* Budget position */}
            {/* One line per budget the AOR draws on (split funding): each is
                charged only its own share of the PO. */}
            {aorDetail.budgetCheck && (
              <Card style={{ marginBottom: 16, background: C.successBg, border: `1px solid ${C.successLight}` }}>
                {(aorDetail.budgetChecks && aorDetail.budgetChecks.length ? aorDetail.budgetChecks : [aorDetail.budgetCheck]).map((c, i) => (
                  <div key={c.budgetRef} style={{ display: "flex", alignItems: "center", gap: 10, marginTop: i > 0 ? 10 : 0 }}>
                    <span style={{ fontSize: text.h3 }}>💰</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: text.md, fontWeight: 700, color: C.success }}>Budget: {c.budgetRef}</div>
                      <div style={{ fontSize: text.sm, color: C.gray700 }}>
                        Annual: {fmt(c.annual)} • Available (excl. this AOR): {fmt(c.available)} • This PO: {fmt(c.charge)} • After PO: {fmt(c.after)}
                      </div>
                    </div>
                    <Badge status={c.status} />
                  </div>
                ))}
              </Card>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <Btn onClick={handleCreate} disabled={submitting || !vendor.trim()}>
                {submitting ? "Creating PO..." : "Create PO — " + fmt(aorDetail.total)}
              </Btn>
              <Btn variant="outline" onClick={() => { setShowForm(false); setSelectedAOR(null); setVendor(""); }}>Cancel</Btn>
            </div>
          </>
        )}
      </div>
    );
  }

  // ─── Detail View ───
  // A sel the loaded list doesn't contain (deep link outside the user's data
  // scope, or a stale id) falls through to the list view.
  const selPo = sel ? pos.find(p => p.id === sel) : null;
  if (selPo) {
    const po = selPo;
    const bs = po.budgetSummary;
    const editable = po.status === "DRAFT" || po.status === "RETURNED";

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <Btn variant="outline" onClick={() => setSel(null)} style={{ padding: "4px 10px", fontSize: text.xs }}>← Back</Btn>
          <h1 style={{ fontSize: text.h2, fontWeight: 700, margin: 0 }}>{po.poNumber}</h1>
          <Badge status={po.status} />
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            {editable && <Btn variant="success" onClick={() => handleSubmitPO(po.id)}>Submit for Approval</Btn>}
          </div>
        </div>

        <Notice notice={notice} onDismiss={() => setNotice(null)} />

        <ReturnedBanner status={po.status} approvals={po.approvals} />

        {/* PO lifecycle, spelled out once (UX-34): the ISSUED hop otherwise
            appears from nowhere between APPROVED and the GRN statuses. */}
        {["ISSUED", "PARTIALLY_RECEIVED", "FULFILLED"].includes(po.status) && (
          <div style={{ fontSize: text.sm, color: C.gray500, marginBottom: 12 }}>
            Lifecycle: final approval issues the PO to the vendor automatically (Approved → Issued); goods receipts then move it to Partially received / Fulfilled.
          </div>
        )}

        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, fontSize: text.md }}>
            <div><span style={{ color: C.gray500 }}>Linked AOR:</span> {onOpenDoc && po.aor?.id ? <DocLink onClick={() => onOpenDoc("aor", po.aor.id)}>{po.aor.aorNumber}</DocLink> : <strong style={{ color: C.primary }}>{po.aor?.aorNumber}</strong>}</div>
            <div><span style={{ color: C.gray500 }}>Vendor:</span> <strong>{po.vendor}</strong></div>
            <div><span style={{ color: C.gray500 }}>Business Unit:</span> <strong>{po.businessUnit}</strong></div>
            <div><span style={{ color: C.gray500 }}>Budget Ref:</span> <strong style={{ color: C.primary }}>{po.budgetRef}</strong></div>
            <div><span style={{ color: C.gray500 }}>Net:</span> <strong>{fmt(po.total)}</strong></div>
            <div><span style={{ color: C.gray500 }}>GST:</span> <strong>{fmt(po.gstTotal || 0)}</strong></div>
            <div><span style={{ color: C.gray500 }}>Gross:</span> <strong style={{ fontSize: text.h3 }}>{fmt(po.grossTotal || po.total)}</strong></div>
            <div><span style={{ color: C.gray500 }}>Date:</span> <strong>{new Date(po.createdAt).toLocaleDateString()}</strong></div>
            <div><span style={{ color: C.gray500 }}>Requestor:</span> <strong>{po.requestor?.displayName}</strong></div>
          </div>
        </Card>

        {/* Vendor-selection context (UX-49): why this vendor, straight from the
            evaluation the PO was created from, with links along the chain. */}
        {po.vendorEvaluation && (
          <Card style={{ marginBottom: 16, border: `1px solid ${C.purpleBorder || C.gray200}` }}>
            <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 8 }}>Vendor selection</div>
            <div style={{ fontSize: text.md, color: C.gray700, lineHeight: 1.7 }}>
              <div>
                This PO was created from vendor evaluation{" "}
                {onOpenDoc ? <DocLink onClick={() => onOpenDoc("vendor-eval", po.vendorEvaluation.id)}>{po.vendorEvaluation.evaluationNumber}</DocLink> : <strong>{po.vendorEvaluation.evaluationNumber}</strong>}
                {po.aor?.tenderRequest && (
                  <> (tender {onOpenDoc ? <DocLink onClick={() => onOpenDoc("tender-requests", po.aor.tenderRequest.id)}>{po.aor.tenderRequest.itqNumber || po.aor.tenderRequest.requestNumber}</DocLink> : <strong>{po.aor.tenderRequest.itqNumber || po.aor.tenderRequest.requestNumber}</strong>})</>
                )}.
              </div>
              {po.vendorEvaluation.awardedBid && (
                <div>Awarded to <strong>{po.vendorEvaluation.awardedBid.vendorName}</strong> at <strong>{fmt(po.vendorEvaluation.awardedBid.price)}</strong> — the PO total is this awarded amount (the AOR's {fmt(po.aor?.total)} was the pre-tender estimate).</div>
              )}
              {po.vendorEvaluation.awardReason && <div><strong>Award note:</strong> {po.vendorEvaluation.awardReason}</div>}
              <div style={{ fontSize: text.sm, color: C.gray500 }}>Open the evaluation for the full consolidated ranking and scores.</div>
            </div>
          </Card>
        )}
        {!po.vendorEvaluation && po.aor?.tenderRequest && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: text.sm, color: C.gray700 }}>
              Related tender: {onOpenDoc ? <DocLink onClick={() => onOpenDoc("tender-requests", po.aor.tenderRequest.id)}>{po.aor.tenderRequest.itqNumber || po.aor.tenderRequest.requestNumber}</DocLink> : <strong>{po.aor.tenderRequest.itqNumber || po.aor.tenderRequest.requestNumber}</strong>}
            </div>
          </Card>
        )}

        {/* One block per budget line the PO draws on (split funding), each
            charged only its own share; `bs` is the tightest of them. */}
        {bs && (po.budgetSummaries && po.budgetSummaries.length ? po.budgetSummaries : [bs]).map((s, i, all) => (
          <Card key={s.budgetRef} style={{ padding: 0, marginBottom: 16, border: `2px solid ${s.status === "OVER_BUDGET" ? C.dangerLight : C.successLight}`, overflow: "hidden" }}>
            <div style={{ padding: "12px 18px", background: s.status === "OVER_BUDGET" ? C.dangerBg : C.successBg, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: text.h3 }}>💰</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: text.md, fontWeight: 700, color: s.status === "OVER_BUDGET" ? C.danger : C.success }}>
                  PO Budget Summary{all.length > 1 ? ` (${i + 1} of ${all.length})` : ""}
                </div>
                <div style={{ fontSize: text.sm, color: C.gray700 }}>
                  {s.bu} — {s.category} • {s.budgetRef}{all.length > 1 ? <> • This PO's share: <strong>{fmt(s.charge)}</strong></> : null}
                </div>
              </div>
              {all.length > 1 && <Badge status={s.status} />}
            </div>
            <div style={{ padding: "14px 18px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, fontSize: text.sm, marginBottom: 12 }}>
                <div><div style={{ color: C.gray500, marginBottom: 2 }}>Annual Budget</div><div style={{ fontSize: text.h3, fontWeight: 700 }}>{fmt(s.annual)}</div></div>
                <div><div style={{ color: C.gray500, marginBottom: 2 }}>Committed (POs)</div><div style={{ fontSize: text.h3, fontWeight: 700, color: C.warning }}>{fmt(s.committedPOs)}</div></div>
                <div><div style={{ color: C.gray500, marginBottom: 2 }}>Utilized</div><div style={{ fontSize: text.h3, fontWeight: 700, color: C.info }}>{fmt(s.utilizedPOs)}</div></div>
                <div><div style={{ color: C.gray500, marginBottom: 2 }}>Approved AORs</div><div style={{ fontSize: text.h3, fontWeight: 700, color: C.purple }}>{fmt(s.approvedAORs)}</div></div>
                <div><div style={{ color: C.gray500, marginBottom: 2 }}>Available (excl. this PO)</div><div style={{ fontSize: text.h3, fontWeight: 700, color: s.available < 0 ? C.danger : C.success }}>{fmt(s.available)}</div></div>
              </div>
              <div style={{ background: C.gray100, borderRadius: 6, height: 16, overflow: "hidden", display: "flex" }}>
                <div style={{ width: `${(s.utilizedPOs / s.annual) * 100}%`, background: C.info, height: "100%" }} />
                <div style={{ width: `${(s.committedPOs / s.annual) * 100}%`, background: C.warning, height: "100%" }} />
                <div style={{ width: `${(s.approvedAORs / s.annual) * 100}%`, background: C.purple, height: "100%" }} />
              </div>
              <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: text.xs, color: C.gray600 }}>
                <span>🔵 Utilized ({fmt(s.utilizedPOs)})</span>
                <span>🟡 Committed ({fmt(s.committedPOs)})</span>
                <span>🟣 Approved AORs ({fmt(s.approvedAORs)})</span>
              </div>
            </div>
          </Card>
        ))}

        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 10 }}>Line Items</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
            <thead><tr>{["Description", "Qty", "Unit Price", "Account", "Tax", "Total"].map((h, i) => (
              <th scope="col" key={i} style={{ padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
            ))}</tr></thead>
            <tbody>
              {po.lineItems?.map((li, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                  <td style={{ padding: "10px 12px" }}>{li.description}</td>
                  <td style={{ padding: "10px 12px" }}>{li.quantity}</td>
                  <td style={{ padding: "10px 12px" }}>{fmt(li.unitPrice)}</td>
                  <td style={{ padding: "10px 12px", color: C.gray600 }}>{li.accountCode || "—"}</td>
                  <td style={{ padding: "10px 12px" }}>
                    {editable ? (
                      <select aria-label={`Tax code — ${li.description}`} value={li.taxCodeId || ""} onChange={e => setLineTax(po.id, li.id, e.target.value)} style={{ padding: "4px 6px", border: `1px solid ${C.gray300}`, borderRadius: 6 }}>
                        {taxCodes.filter(t => t.isActive).map(t => <option key={t.id} value={t.id}>{t.code} ({t.rate}%)</option>)}
                      </select>
                    ) : (
                      taxCodes.find(t => t.id === li.taxCodeId)?.code || "—"
                    )}
                  </td>
                  <td style={{ padding: "10px 12px", fontWeight: 600 }}>{fmt(li.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {editable && (
            <div style={{ marginTop: 12, textAlign: "right" }}>
              <Btn onClick={() => saveTaxCodes(po)}>Save Tax Codes</Btn>
            </div>
          )}
        </Card>

        <ApprovalTrail approvals={po.approvals} docType="PO" />


        <Card>
          <DocumentSection
            entityType="po"
            entityId={po.id}
            documents={po.documents || []}
            checklistItems={po.checklist || []}
            onChange={load}
          />
        </Card>

        <WatcherPanel docType="PO" docId={po.id} canManage={!!me && (me.id === po.requestorId || me.roleName === "Administrator")} />
      </div>
    );
  }

  // ─── List View ───
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: text.h1, fontWeight: 700, color: C.gray1100, margin: 0 }}>Purchase Orders</h1>
          <p style={{ fontSize: text.md, color: C.gray500, margin: "2px 0 0" }}>{pos.length} records</p>
        </div>
        <Btn onClick={() => { setNotice(null); setShowForm(true); }}>+ New PO</Btn>
      </div>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
          <thead><tr>{["PO No.", "Date", "AOR Ref", "Vendor", "Business Unit", "Budget Ref", "Total", "Status"].map((h, i) => (
            <th scope="col" key={i} style={{ padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
          ))}</tr></thead>
          <tbody>
            {pos.map(po => (
              <tr key={po.id} {...rowAction(() => { setNotice(null); setSel(po.id); })} style={{ cursor: "pointer", borderBottom: `1px solid ${C.gray100}` }}
                onMouseEnter={e => e.currentTarget.style.background = C.gray50}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ padding: "10px 12px", fontWeight: 600, color: C.primary }}>{po.poNumber}</td>
                <td style={{ padding: "10px 12px" }}>{new Date(po.createdAt).toLocaleDateString()}</td>
                <td style={{ padding: "10px 12px", color: C.primary }}>{po.aor?.aorNumber}</td>
                <td style={{ padding: "10px 12px" }}>{po.vendor}</td>
                <td style={{ padding: "10px 12px" }}>{po.businessUnit}</td>
                <td style={{ padding: "10px 12px", fontSize: text.xs, color: C.primary }}>{po.budgetRef}</td>
                <td style={{ padding: "10px 12px" }}>{fmt(po.total)}</td>
                <td style={{ padding: "10px 12px" }}><Badge status={po.status} /></td>
              </tr>
            ))}
            {pos.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 30, textAlign: "center", color: C.gray500 }}>No purchase orders yet — create one from an approved AOR with + New PO.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
